import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const API = axios.create({ baseURL: '/api', withCredentials: true });

// ── Count-up animation for stat numbers ──
const CountUp = ({ value, prefix = '', decimals = 0, duration = 900 }) => {
  const [display, setDisplay] = useState(0);
  const rafRef = useRef();

  useEffect(() => {
    const target = parseFloat(value) || 0;
    const start = performance.now();
    const tick = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      // ease-out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      setDisplay(target * eased);
      if (progress < 1) rafRef.current = requestAnimationFrame(tick);
      else setDisplay(target);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, [value, duration]);

  const formatted = display.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  return <span>{prefix}{formatted}</span>;
};

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const canSeeMoney = user?.role === 'boss' || user?.role === 'admin';
  const [customer, setCustomer] = useState(null);
  const [stats, setStats] = useState(null);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('selected_customer');
    if (!stored) { navigate('/select-customer'); return; }
    const c = JSON.parse(stored);
    setCustomer(c);
    fetchDashboardData(c.customer_id);
  }, [navigate]);

  const fetchDashboardData = async (customerId) => {
    try {
      const res = await API.get(`/invoices?customer_id=${customerId}`);
      const invoices = res.data;

      const now = new Date();
      const thisMonth = invoices.filter(inv => {
        const d = new Date(inv.invoice_date);
        return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
      });
      const lastMonth = invoices.filter(inv => {
        const d = new Date(inv.invoice_date);
        const last = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        return d.getMonth() === last.getMonth() && d.getFullYear() === last.getFullYear();
      });

      const thisMonthTotal = thisMonth.reduce((sum, inv) => sum + parseFloat(inv.amount_due), 0);
      const lastMonthTotal = lastMonth.reduce((sum, inv) => sum + parseFloat(inv.amount_due), 0);
      const allTimeTotal = invoices.reduce((sum, inv) => sum + parseFloat(inv.amount_due), 0);

      setStats({
        totalInvoices: invoices.length,
        thisMonthCount: thisMonth.length,
        thisMonthTotal,
        lastMonthTotal,
        allTimeTotal,
        change: lastMonthTotal > 0
          ? (((thisMonthTotal - lastMonthTotal) / lastMonthTotal) * 100).toFixed(1)
          : null
      });

      setRecentInvoices(invoices.slice(0, 8));
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  };

  if (!customer) return null;

  // value can be a number (animated) — accent is the left bar color
  const statCard = (label, valueNode, sub, accent) => (
    <div
      className="fcc-hover-lift"
      style={{
        background: 'white', border: '1px solid #E2E8F0', borderRadius: '10px',
        padding: '18px 20px', borderLeft: `4px solid ${accent || '#F5A800'}`
      }}
    >
      <p style={{ fontSize: '13px', color: '#5B6B7F', marginBottom: '6px' }}>{label}</p>
      <p style={{ fontSize: '26px', fontWeight: '700', color: '#0F1B2D', lineHeight: 1.1 }}>{valueNode}</p>
      {sub && <p style={{ fontSize: '12px', color: '#8595A6', marginTop: '6px' }}>{sub}</p>}
    </div>
  );

  // admin items as a distinct panel (icon tile + label + desc + chevron)
  const adminItems = [
    { icon: '💲', label: 'Zone Rates', desc: 'Edit pricing matrix', path: `/admin/zone-rates/${customer.customer_id}`, roles: ['boss', 'admin'] },
    { icon: '📍', label: 'ZIP Data', desc: 'Manage ZIP zones', path: `/admin/zip-data/${customer.customer_id}`, roles: ['boss', 'admin'] },
    { icon: '⚙️', label: 'Settings', desc: 'Billing settings', path: `/admin/settings/${customer.customer_id}`, roles: ['boss', 'admin'] },
    { icon: '👥', label: 'Customers', desc: 'Manage customers', path: '/admin/customers', roles: ['admin'] },
    { icon: '🔑', label: 'Users', desc: 'Approve & manage access', path: '/admin/users', roles: ['admin'] },
  ].filter(item => item.roles.includes(user?.role));

  return (
    <div className="fcc-fade-in" style={{ padding: '24px', maxWidth: '1200px', margin: '0 auto' }}>

      {/* Welcome */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '22px', fontWeight: '600' }}>Dashboard</h1>
        <p style={{ color: '#5B6B7F', fontSize: '14px', marginTop: '4px' }}>
          {customer.name} · {new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
        </p>
      </div>

      {/* Quick actions */}
      <div style={{ display: 'flex', gap: '12px', marginBottom: '28px' }}>
        <button
          onClick={() => navigate('/invoices/new')}
          className="btn btn-primary fcc-press"
          style={{ width: 'auto', padding: '10px 24px', fontSize: '14px' }}
        >
          + New Invoice
        </button>
        <button
          onClick={() => navigate('/invoices')}
          className="fcc-press"
          style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', padding: '10px 24px', cursor: 'pointer', fontSize: '14px', color: '#0F1B2D' }}
        >
          Search Invoices
        </button>
      </div>

      {/* Stats */}
      {loading ? (
        <div style={{ color: '#5B6B7F', marginBottom: '28px' }}>Loading stats...</div>
      ) : stats && (
        <div className="fcc-stagger" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px', marginBottom: '28px' }}>
          {statCard(
            'This Month — Invoices',
            <CountUp value={stats.thisMonthCount} />,
            `${new Date().toLocaleString('default', { month: 'long' })} ${new Date().getFullYear()}`,
            '#F5A800'
          )}
          {canSeeMoney && statCard(
            'This Month — Billed',
            <CountUp value={stats.thisMonthTotal} prefix="$" decimals={2} />,
            stats.change !== null
              ? `${stats.change > 0 ? '▲' : '▼'} ${Math.abs(stats.change)}% vs last month`
              : 'No data for last month',
            '#15803D'
          )}
          {canSeeMoney && statCard(
            'Last Month — Billed',
            <CountUp value={stats.lastMonthTotal} prefix="$" decimals={2} />,
            'Previous month total',
            '#8595A6'
          )}
          {statCard(
            'All Time — Total Invoices',
            <CountUp value={stats.totalInvoices} />,
            'All invoices on record',
            '#1A2A3A'
          )}
          {canSeeMoney && statCard(
            'All Time — Total Billed',
            <CountUp value={stats.allTimeTotal} prefix="$" decimals={2} />,
            'Cumulative amount',
            '#3A3C3F'
          )}
        </div>
      )}

      {/* Recent invoices */}
      <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
        <div style={{ padding: '14px 20px', borderBottom: '1px solid #E2E8F0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h2 style={{ fontSize: '15px', fontWeight: '600' }}>Recent Invoices</h2>
          <button onClick={() => navigate('/invoices')} className="fcc-press" style={{ background: 'none', border: 'none', color: '#F5A800', cursor: 'pointer', fontSize: '13px' }}>
            View all →
          </button>
        </div>
        {loading ? (
          <div style={{ padding: '24px', color: '#5B6B7F', textAlign: 'center' }}>Loading...</div>
        ) : recentInvoices.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: '#5B6B7F' }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>No invoices yet</p>
            <p style={{ fontSize: '14px', marginBottom: '16px' }}>Create your first invoice to get started</p>
            <button onClick={() => navigate('/invoices/new')} className="btn btn-primary fcc-press" style={{ width: 'auto', padding: '8px 20px' }}>
              Create Invoice
            </button>
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
            <thead>
              <tr>
                {['Invoice #', 'Date', 'Bill To', ...(canSeeMoney ? ['Amount Due'] : []), 'Actions'].map(h => (
                  <th key={h} style={{ padding: '10px 16px', background: '#F4F7FB', color: '#5B6B7F', fontSize: '12px', fontWeight: '500', textAlign: 'left', borderBottom: '1px solid #E2E8F0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentInvoices.map(inv => (
                <tr
                  key={inv.invoice_id}
                  className="fcc-row-hover"
                  style={{ cursor: 'pointer' }}
                  onClick={() => navigate(`/invoices/${inv.invoice_id}`)}
                >
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: '600', color: '#F5A800' }}>
                    {inv.invoice_number}
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', color: '#5B6B7F' }}>
                    {new Date(inv.invoice_date).toLocaleDateString()}
                  </td>
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                    {inv.bill_to}
                  </td>
                  {canSeeMoney && (
                    <td style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0', fontWeight: '600', color: '#15803D' }}>
                      ${parseFloat(inv.amount_due).toFixed(2)}
                    </td>
                  )}
                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #f0f0f0' }}>
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/invoices/${inv.invoice_id}`); }}
                      className="fcc-press"
                      style={{ background: '#F5A800', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px', marginRight: '6px' }}
                    >
                      View
                    </button>
                    <button
                      onClick={e => { e.stopPropagation(); navigate(`/invoices/${inv.invoice_id}/edit`); }}
                      className="fcc-press"
                      style={{ background: '#f59e0b', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer', fontSize: '12px' }}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Admin panel — distinct list style, not nav-like cards */}
      {adminItems.length > 0 && (
        <div style={{ marginTop: '28px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <h2 style={{ fontSize: '13px', fontWeight: '600', color: '#5B6B7F', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Administration</h2>
            <div style={{ flex: 1, height: '1px', background: '#E2E8F0' }} />
          </div>
          <div style={{ background: 'white', border: '1px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
            {adminItems.map((item, idx) => (
              <div
                key={item.path}
                className="fcc-row-hover"
                onClick={() => navigate(item.path)}
                style={{
                  display: 'flex', alignItems: 'center', gap: '14px',
                  padding: '14px 18px', cursor: 'pointer',
                  borderBottom: idx < adminItems.length - 1 ? '1px solid #EDF1F6' : 'none'
                }}
              >
                <div style={{
                  width: '38px', height: '38px', borderRadius: '8px',
                  background: '#FDF4DD', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', fontSize: '18px', flexShrink: 0
                }}>
                  {item.icon}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: '600', fontSize: '14px', color: '#0F1B2D' }}>{item.label}</p>
                  <p style={{ fontSize: '12px', color: '#8595A6', marginTop: '1px' }}>{item.desc}</p>
                </div>
                <span style={{ color: '#8595A6', fontSize: '20px' }}>›</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Dashboard;
